import { getResponsiveFontStyles } from '../sections/Commons';
import { GlobalStyles } from '@zeus/shared';
import { getGlobalStyleValue, getNestedElementDisplayValue } from './mediaStyles';

export const useResponsiveStyles = (isDesktop: boolean, localStyles: any, globalStyles?: GlobalStyles) => {
  // Ensure CSS length has px when a numeric or numeric-string without unit is provided
  const ensurePx = (value: any) => {
    if (value === undefined || value === null || value === '') return value;
    if (typeof value === 'number') return `${value}px`;
    const str = String(value).trim();
    if (/^-?\d+(\.\d+)?$/.test(str) && !str.endsWith('px')) return `${str}px`;
    return str;
  };

  /**
   * Creates mobile media styles for main elements
   */
  const createMainElementMobileStyles = (styleType: string, baseStyles: any, globalStyleKeyMapping?: string) => {
    const fontSize = localStyles?.[styleType]?.fontSize as string;
    const responsiveStyles = getResponsiveFontStyles(
      fontSize || getGlobalStyleValue(styleType, 'fontSize', globalStyles, globalStyleKeyMapping)
    );

    return {
      fontSize:
        localStyles['media-s']?.[styleType]?.fontSize ||
        responsiveStyles?.mobile?.fontSize ||
        localStyles?.[styleType]?.fontSize ||
        getGlobalStyleValue(styleType, 'fontSize', globalStyles, globalStyleKeyMapping) ||
        baseStyles.fontSize,
      fontWeight:
        localStyles['media-s']?.[styleType]?.fontWeight ||
        localStyles?.[styleType]?.fontWeight ||
        getGlobalStyleValue(styleType, 'fontWeight', globalStyles, globalStyleKeyMapping) ||
        baseStyles.fontWeight,
      lineHeight:
        localStyles['media-s']?.[styleType]?.lineHeight ||
        responsiveStyles?.mobile?.lineHeight ||
        localStyles?.[styleType]?.lineHeight ||
        getGlobalStyleValue(styleType, 'lineHeight', globalStyles, globalStyleKeyMapping) ||
        baseStyles.lineHeight,
      letterSpacing:
        localStyles['media-s']?.[styleType]?.letterSpacing ||
        localStyles?.[styleType]?.letterSpacing ||
        getGlobalStyleValue(styleType, 'letterSpacing', globalStyles, globalStyleKeyMapping) ||
        baseStyles.letterSpacing,
      textStroke:
        localStyles['media-s']?.[styleType]?.textStroke ||
        localStyles?.[styleType]?.textStroke ||
        baseStyles.textStroke,
      fontStyle:
        localStyles['media-s']?.[styleType]?.fontStyle ||
        localStyles?.[styleType]?.fontStyle ||
        getGlobalStyleValue(styleType, 'fontStyle', globalStyles, globalStyleKeyMapping) ||
        baseStyles.fontStyle,
      effect: localStyles['media-s']?.[styleType]?.effect || localStyles?.[styleType]?.effect || baseStyles.effect,
      effectColor: localStyles?.[styleType]?.effectColor || baseStyles.effectColor,
      effectIntensity:
        localStyles['media-s']?.[styleType]?.effectIntensity ||
        localStyles?.[styleType]?.effectIntensity ||
        baseStyles.effectIntensity,
    };
  };

  /**
   * Creates mobile media styles for nested elements
   */
  const createNestedElementMobileStyles = (
    styleType: string,
    baseStyles: any,
    cardIndex: number,
    globalStyleKeyMapping?: string,
    nestedElementStyles?: any
  ) => {
    const mediaKey = 'media-s';
    const parentStyles = localStyles;
    const actualNestedStyles = nestedElementStyles || localStyles.cards?.items?.[cardIndex];

    // Get nested element display values for mobile
    const getNestedMobileValue = (property: string) => {
      return getNestedElementDisplayValue(
        property,
        mediaKey,
        actualNestedStyles,
        parentStyles,
        globalStyles,
        styleType,
        globalStyleKeyMapping
      );
    };

    const _mobileFontSize = getNestedMobileValue('fontSize');
    const normalizedMobileFontSize =
      _mobileFontSize !== undefined && _mobileFontSize !== null && _mobileFontSize !== ''
        ? ensurePx(_mobileFontSize)
        : baseStyles.fontSize;

    const mobileLineHeightRaw = getNestedMobileValue('lineHeight');
    const normalizedMobileLineHeight = mobileLineHeightRaw ? ensurePx(mobileLineHeightRaw) : normalizedMobileFontSize;

    return {
      fontSize: normalizedMobileFontSize,
      lineHeight: normalizedMobileLineHeight,
      fontWeight: getNestedMobileValue('fontWeight') || baseStyles.fontWeight,
      fontStyle: getNestedMobileValue('fontStyle') || baseStyles.fontStyle,
      letterSpacing: getNestedMobileValue('letterSpacing')
        ? `${getNestedMobileValue('letterSpacing')}`
        : baseStyles.letterSpacing,
      inlineText: getNestedMobileValue('inlineText'),
      effect: getNestedMobileValue('effect') || baseStyles.effect,
      textStroke: getNestedMobileValue('textStroke') || baseStyles.textStroke,
      effectColor: getNestedMobileValue('effectColor') || baseStyles.effectColor,
      effectIntensity: getNestedMobileValue('effectIntensity') || baseStyles.effectIntensity,
    };
  };

  /**
   * Creates responsive styles for any style type
   */
  const createResponsiveStyles = (
    baseStyles: Record<string, unknown>,
    styleType: string,
    globalStyleKeyMapping?: string,
    mobileStyles?: Record<string, unknown>,
    nestedOptions?: {
      isNested: boolean;
      cardIndex: number;
      nestedElementStyles?: any;
      mobileStyles?: Record<string, unknown>;
    }
  ) => {
    // For nested elements, use the nested hierarchy
    if (nestedOptions?.isNested && nestedOptions.cardIndex !== undefined) {
      const nestedElementStyles =
        nestedOptions.nestedElementStyles || localStyles.cards?.items?.[nestedOptions.cardIndex];
      const parentStyles = localStyles;

      // Get nested element display values using the established hierarchy
      const getNestedValue = (property: string) => {
        return getNestedElementDisplayValue(
          property,
          undefined, // desktop
          nestedElementStyles,
          parentStyles,
          globalStyles,
          styleType,
          globalStyleKeyMapping
        );
      };

      // Create nested element styles
      const fontSizeRaw = getNestedValue('fontSize');
      const lineHeight = getNestedValue('lineHeight');
      const fontWeight = getNestedValue('fontWeight');
      const fontStyle = getNestedValue('fontStyle');
      const letterSpacing = getNestedValue('letterSpacing');
      const color = getNestedValue('color');
      const effect = getNestedValue('effect');
      const textStroke = getNestedValue('textStroke');

      const normalizedDesktopFontSize =
        fontSizeRaw !== undefined && fontSizeRaw !== null && fontSizeRaw !== ''
          ? ensurePx(fontSizeRaw)
          : baseStyles.fontSize;

      const desktopStyles = {
        ...baseStyles,
        fontSize: normalizedDesktopFontSize,
        lineHeight: lineHeight ? ensurePx(lineHeight) : baseStyles.lineHeight,
        fontWeight: fontWeight || baseStyles.fontWeight,
        fontStyle: fontStyle || baseStyles.fontStyle,
        letterSpacing: letterSpacing ? ensurePx(letterSpacing) : baseStyles.letterSpacing,
        color: color || baseStyles.color, // Color is not responsive - same for desktop and mobile
        effect: effect || baseStyles.effect,
        textStroke: textStroke || baseStyles.textStroke,
        effectColor: getNestedValue('effectColor') || baseStyles.effectColor,
        effectIntensity: getNestedValue('effectIntensity') || baseStyles.effectIntensity,
        inlineText: getNestedValue('inlineText'),
      };

      // Create mobile styles for nested elements
      const nestedMobileStyles = createNestedElementMobileStyles(
        styleType,
        baseStyles,
        nestedOptions.cardIndex,
        globalStyleKeyMapping,
        nestedElementStyles
      );

      // Use provided mobileStyles from nestedOptions if available
      const finalMobileStyles = nestedOptions.mobileStyles
        ? { ...nestedMobileStyles, ...nestedOptions.mobileStyles }
        : nestedMobileStyles;

      if (isDesktop) {
        return {
          ...desktopStyles,
          '@media (max-width: 780px)': finalMobileStyles,
        };
      }

      return {
        ...desktopStyles,
        ...finalMobileStyles,
        '@media (max-width: 780px)': finalMobileStyles,
      };
    }

    // For main elements, use the existing responsive logic
    const mainMobileStyles = createMainElementMobileStyles(styleType, baseStyles, globalStyleKeyMapping);

    if (isDesktop) {
      return {
        ...baseStyles,
        '@media (max-width: 780px)': mainMobileStyles,
      };
    }

    return {
      ...baseStyles,
      ...mobileStyles, // Use provided mobileStyles if available (backward compatibility)
      ...mainMobileStyles,
      '@media (max-width: 780px)': mainMobileStyles,
    };
  };

  return {
    createResponsiveStyles,
  };
};
